import { useState } from 'react';
import { Roles } from '@touristation/shared';
import { useAuth } from '../auth/AuthProvider';
import { useCreateTool, useDeleteTool, useTools, useUpdateTool, type ToolInput } from '../api/hooks';
import type { Tool } from '../api/types';
import { useT } from '../i18n/LangProvider';
import { Icon } from '../ui/Icon';
import { useConfirm } from '../ui/confirm';

const COLORS = ['orange', 'purple', 'magenta', 'neutral'];
const emptyTool: ToolInput = { name: '', category: '', href: '', description: '', icon: '', color: 'purple', kind: '', tag: '' };

function ToolEditor({ initial, editingId, onClose }: { initial: ToolInput; editingId: string | null; onClose: () => void }) {
  const tr = useT();
  const create = useCreateTool();
  const update = useUpdateTool();
  const [f, setF] = useState<ToolInput>(initial);
  const busy = create.isPending || update.isPending;

  async function save() {
    if (!f.name.trim() || !f.category.trim() || !f.href.trim()) return;
    const body: ToolInput = {
      name: f.name.trim(), category: f.category.trim(), href: f.href.trim(),
      description: f.description?.trim() || undefined, icon: f.icon?.trim() || undefined,
      color: f.color, kind: f.kind || undefined, tag: f.tag?.trim() || undefined,
    };
    if (editingId) await update.mutateAsync({ id: editingId, ...body });
    else await create.mutateAsync(body);
    onClose();
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', display: 'grid', placeItems: 'center', zIndex: 100, padding: 20 }}>
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: 'min(560px, 100%)', padding: 0 }}>
        <div className="spaced" style={{ padding: '14px 18px', borderBottom: '1px solid var(--line,#eee)' }}>
          <strong>{editingId ? tr('Modifica tool') : tr('Nuovo tool')}</strong>
          <button className="btn btn--ghost btn--icon" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="row" style={{ gap: 12, alignItems: 'center' }}>
            <span style={{ width: 48, height: 48, borderRadius: 10, display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 15, color: '#fff', background: `var(--t-${f.color || 'purple'}, #7B2D8E)` }}>
              {f.icon || f.name.slice(0, 2).toUpperCase() || '??'}
            </span>
            <div style={{ flex: 1 }}>
              <label className="label">{tr('Icona (2 lettere o emoji)')}</label>
              <input className="input" maxLength={8} value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} placeholder={tr('es. WM oppure 🎫')} />
            </div>
            <div>
              <label className="label">{tr('Colore')}</label>
              <div className="row" style={{ gap: 6 }}>
                {COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setF({ ...f, color: c })} title={c}
                          style={{ width: 26, height: 26, borderRadius: 7, background: `var(--t-${c}, #888)`, border: f.color === c ? '2px solid var(--ink)' : '2px solid transparent', cursor: 'pointer' }} />
                ))}
              </div>
            </div>
          </div>
          <div className="field"><label className="label">{tr('Nome')}</label><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={tr('Nome del tool')} /></div>
          <div className="field"><label className="label">{tr('Descrizione')}</label><input className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder={tr('Breve descrizione')} /></div>
          <div className="row" style={{ gap: 12 }}>
            <div className="field" style={{ flex: 1 }}><label className="label">{tr('Categoria')}</label><input className="input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder={tr('es. Software interno')} /></div>
            <div className="field" style={{ flex: 1 }}>
              <label className="label">{tr('Tipo')}</label>
              <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
                <option value="">{tr('Link / sito web')}</option>
                <option value="download">{tr('Download')}</option>
              </select>
            </div>
          </div>
          <div className="field"><label className="label">{tr('URL')}</label><input className="input" value={f.href} onChange={(e) => setF({ ...f, href: e.target.value })} placeholder="https://…" /></div>
          <div className="field"><label className="label">{tr('Tag (opzionale)')}</label><input className="input" value={f.tag} onChange={(e) => setF({ ...f, tag: e.target.value })} placeholder={tr('es. Estensione Chrome')} /></div>
        </div>
        <div className="spaced" style={{ padding: 14, borderTop: '1px solid var(--line,#eee)' }}>
          <span />
          <div className="cluster">
            <button className="btn" onClick={onClose}>{tr('Annulla')}</button>
            <button className="btn btn--brand" onClick={save} disabled={busy}>{tr('Salva')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ToolsScreen() {
  const tr = useT();
  const confirm = useConfirm();
  const auth = useAuth();
  const isAdmin = auth.hasRole(Roles.ADMIN);
  const { data, isLoading, isError, error } = useTools();
  const del = useDeleteTool();
  const [editor, setEditor] = useState<{ initial: ToolInput; id: string | null } | null>(null);

  if (isLoading) return <div className="muted" style={{ padding: 24 }}>{tr('Caricamento…')}</div>;
  if (isError) return <div style={{ padding: 24, color: 'var(--danger, #c00)' }}>{tr('Errore:')} {String(error)}</div>;

  const tools = data?.items ?? [];
  const byCategory = tools.reduce<Record<string, Tool[]>>((acc, t) => { (acc[t.category] ??= []).push(t); return acc; }, {});
  const sections = Object.entries(byCategory).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="tools-screen" style={{ padding: 24 }}>
      <div className="page-head" style={{ marginBottom: 18 }}>
        <div><h1 className="h1">{tr('Tool & programmi')}</h1><div className="page-head__sub">{tr('Strumenti e collegamenti operativi.')}</div></div>
        {isAdmin && <button className="btn btn--brand" onClick={() => setEditor({ initial: { ...emptyTool }, id: null })}><Icon name="plus" size={14} /> {tr('Nuovo tool')}</button>}
      </div>

      {tools.length === 0 && (
        <div className="card" style={{ padding: 24 }}>
          <h2 className="h2">{tr('Nessuno strumento configurato')}</h2>
          <p className="muted">{isAdmin ? tr('Aggiungi il primo con “Nuovo tool”.') : tr("L'elenco viene popolato dagli amministratori.")}</p>
        </div>
      )}

      <div className="tools-sections" style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
        {sections.map(([category, items]) => (
          <section key={category}>
            <div className="spaced" style={{ marginBottom: 12 }}>
              <h2 className="h2" style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ink-3)' }}>{category}</h2>
              <span className="muted" style={{ fontSize: 12 }}>{tr(items.length === 1 ? '{n} strumento' : '{n} strumenti', { n: items.length })}</span>
            </div>
            <div className="tools-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
              {items.map((t) => (
                <div key={t.id} className="card tools-card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, position: 'relative' }}>
                  {isAdmin && (
                    <div className="row" style={{ gap: 4, position: 'absolute', top: 8, right: 8 }}>
                      <button className="btn btn--ghost btn--icon" title={tr('Modifica')} onClick={() => setEditor({ initial: { name: t.name, category: t.category, href: t.href, description: t.description ?? '', icon: t.icon ?? '', color: t.color ?? 'purple', kind: t.kind ?? '', tag: t.tag ?? '' }, id: t.id })}><Icon name="edit" size={13} /></button>
                      <button className="btn btn--ghost btn--icon" title={tr('Elimina')} onClick={async () => { if (await confirm({ message: tr('Eliminare "{name}"?', { name: t.name }), confirmLabel: tr('Elimina'), danger: true })) del.mutate(t.id); }}><Icon name="trash" size={13} /></button>
                    </div>
                  )}
                  <a href={t.href} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div className="row" style={{ gap: 12, alignItems: 'center' }}>
                      <span style={{ width: 40, height: 40, borderRadius: 10, display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 13, color: '#fff', flexShrink: 0, background: `var(--t-${t.color ?? 'purple'}, #7B2D8E)` }}>
                        {t.icon ?? t.name.slice(0, 2).toUpperCase()}
                      </span>
                      <div style={{ minWidth: 0, flex: 1, paddingRight: isAdmin ? 44 : 0 }}>
                        <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>{t.name}<Icon name={t.kind === 'download' ? 'download' : 'external'} size={13} /></div>
                        <div className="muted" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description}</div>
                      </div>
                    </div>
                    {t.tag && <div><span className="badge is-info">{t.tag}</span></div>}
                  </a>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {editor && <ToolEditor initial={editor.initial} editingId={editor.id} onClose={() => setEditor(null)} />}
    </div>
  );
}
